import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Box, chooseColor, Icon, useForm, FieldData, LoadingIndicator } from 'vopay';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useLayout } from '@/providers/Layout.provider';
import { usePaylink } from '@/providers/Paylink.provider';
import { useToken } from '@/providers/Token.provider';
import { useDigitalWalletFormRows } from './buildDigitalWalletForm.helper';
import useStorage from '@/hooks/useStorage';
import useLinkDigitalWallet from './useLinkDigitalWallet';
import usePostPaylinkSetAccount from '@/hooks/useApi/usePostPaylinkSetAccount';
import PageTitle from '@/components/UI/PageTitle/PageTitle';
import styles from './DigitalWalletForm.module.scss';

export type DigitalWalletRequest = {
  PaymentMethod: 'EMAIL' | 'PHONE';
  PhoneNumber: string;
  EmailAddress: string;
  FirstName?: string;
  LastName?: string;
  CompanyName?: string;
  AccountType?: 'personal' | 'business';
};

const DigitalWalletForm: FunctionComponent = () => {
  const history = useHistory();
  const storage = useStorage();
  const { t } = useTranslation('translations', { keyPrefix: 'PaymentMethodForm' });
  const { setLayoutInfo } = useLayout();
  const { paylink, Token } = usePaylink();
  const { token } = useToken();
  const { digitalWallet } = useParams<{ digitalWallet: 'paypal' | 'venmo' }>();
  const { linkWallet } = useLinkDigitalWallet();
  const [, , , completeElinx] = usePostPaylinkSetAccount({});
  const [isLoading, setIsLoading] = useState(false);

  const eLinxHasName = useMemo(() => {
    return !!paylink?.ReceiverName;
  }, [paylink?.ReceiverName]);
  const DigitalWalletFormRows = useDigitalWalletFormRows(eLinxHasName, digitalWallet);

  useEffect(() => {
    setLayoutInfo(
      chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
      'curve',
      '',
      80,
      20,
      0,
      true,
      true,
    );
  }, []);

  const defaultValues: DigitalWalletRequest = {
    PaymentMethod: 'EMAIL',
    PhoneNumber: paylink.ReceiverPhoneNumber,
    EmailAddress: paylink.ReceiverEmailAddress,
    AccountType: 'personal',
  };

  const fieldDataList: FieldData<DigitalWalletRequest>[] = [...DigitalWalletFormRows];

  const linkDigitalWallet = async (values: DigitalWalletRequest, method: 'paypal' | 'venmo') => {
    setIsLoading(true);

    try {
      const IframeKey = storage.token.getIQ11Token().iQ11Token;
      const walletParams = {
        RecipientID: values.PaymentMethod === 'EMAIL' ? values.EmailAddress : values.PhoneNumber,
        RecipientName: eLinxHasName
          ? paylink.ReceiverName
          : values.AccountType === 'business'
            ? `${values.CompanyName || ''}`
            : `${values.FirstName || ''} ${values.LastName || ''}`,
        IframeKey: paylink.IframeKey || IframeKey,
        walletType: method,
        recipientType: values.PaymentMethod,
      };
      const paymentMethodToken = await linkWallet(walletParams);
      if (!paymentMethodToken) {
        history.push('/connect/transaction-error');
        return;
      }
      if (!Token) {
        const redirectMethod = token?.RedirectMethod;
        const url = `${token?.RedirectURL || ''}?Token=${paymentMethodToken}&PaymentMethod=${method}`;
        const Response = {
          Step: 'LINK',
          Url: url,
          RecipientName: walletParams.RecipientName,
          RecipientEmailAddress: values.PaymentMethod === 'EMAIL' ? values.EmailAddress : '',
          RecipientPhoneNumber: values.PaymentMethod === 'PHONE' ? values.PhoneNumber : '',
          PaymentMethod: method,
          Token: paymentMethodToken,
        };
        window.top?.postMessage(Response, '*');
        if (window.parent && window.parent !== window.top) {
          window.parent.postMessage(Response, '*');
        }
        setTimeout(() => {
          if (redirectMethod === 'InnerRedirect') {
            window.location.href = url;
          } else if (redirectMethod === 'OuterRedirect') {
            window.top!.location.href = url;
          } else {
            history.push('/success');
          }
        }, 300);
        return;
      }
      const elinxResponse = await completeElinx({
        SingleUseToken: Token,
        Iq11Token: paymentMethodToken,
        PaymentMethod: method,
      });
      if (elinxResponse.Success) {
        history.push('/thank-you', {
          paymentMethodType: method,
          paymentMethod: values.PaymentMethod === 'EMAIL' ? values.EmailAddress : values.PhoneNumber,
          email: values.EmailAddress,
          phone: values.PhoneNumber,
          connectionType: values.PaymentMethod.toLowerCase(),
        });
      } else {
        history.push('/connect/transaction-error');
      }
    } catch (err) {
      if (err instanceof Error) {
        history.push('/connect/transaction-error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const { renderFormElements, ctx } = useForm<DigitalWalletRequest, DigitalWalletRequest>({
    config: fieldDataList,
    initialValues: defaultValues,
    onSubmit: (values) => linkDigitalWallet(values, digitalWallet),
  });

  return (
    <Box flx="col" ai="center" gapSize="58px" boxWidth="100%" maxwidth="404px">
      <LoadingIndicator hasNoMessage hasNoTitle isVisible={isLoading} size="lg" />
      <PageTitle
        title={
          <Box jc="center">
            <div className={styles.logoContainer}>
              <Icon icon={digitalWallet === 'paypal' ? 'paypal2' : 'venmo2'} width="100%" height="100%" color="none" />
            </div>
          </Box>
        }
      />
      <Box flx="col" ai="center" gapSize="32px" width="100%">
        <div className={styles.rows}>{renderFormElements({ config: DigitalWalletFormRows })}</div>
        <Button
          disabled={!ctx.isValid}
          className={styles.button}
          colorScheme="primary"
          onClick={() => {
            ctx.handleSubmit();
          }}
        >
          {t('confirmButton')}
        </Button>
      </Box>
    </Box>
  );
};

export default DigitalWalletForm;
